/**
 * useQualityRating Hook
 *
 * Custom hook for managing quality ratings for a route.
 * Handles fetching the user's rating and submitting/updating ratings.
 * Uses composite key: userId_routeId
 */

import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { getQualityRatingId, type QualityRating, type QualityRatingValue } from '@flash/shared';

/**
 * State interface for quality rating query
 */
interface UseQualityRatingState {
  /** The user's current quality rating (null if none) */
  rating: QualityRating | null;
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useQualityRating hook
 */
interface UseQualityRatingResult extends UseQualityRatingState {
  /** Submit or update a rating for the route */
  submitRating: (rating: QualityRatingValue) => Promise<void>;
  /** Remove the user's rating */
  removeRating: () => Promise<void>;
  /** Whether a submission operation is in progress */
  isSubmitting: boolean;
}

/**
 * Hook to manage quality ratings for a specific route
 *
 * @param routeId - The route ID to manage ratings for
 * @returns Object containing rating state and management functions
 *
 * @example
 * ```tsx
 * const {
 *   rating,
 *   loading,
 *   error,
 *   submitRating,
 *   removeRating,
 *   isSubmitting,
 * } = useQualityRating('route-123');
 *
 * // Submit a rating
 * await submitRating(4);
 *
 * // Remove the rating
 * await removeRating();
 * ```
 */
export const useQualityRating = (routeId: string | undefined): UseQualityRatingResult => {
  const { user } = useAuth();

  const [state, setState] = useState<UseQualityRatingState>({
    rating: null,
    loading: true,
    error: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real-time subscription to user's rating
  useEffect(() => {
    if (!user || !routeId) {
      setState({ rating: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const db = getFirebaseDb();
    const ratingId = getQualityRatingId(user.uid, routeId);
    const ratingRef = doc(db, 'qualityRatings', ratingId);

    // Set up real-time listener
    const unsubscribe: Unsubscribe = onSnapshot(
      ratingRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const rating: QualityRating = {
            id: docSnapshot.id,
            odKey: data['odKey'] as string,
            userId: data['userId'] as string,
            routeId: data['routeId'] as string,
            rating: data['rating'] as QualityRatingValue,
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
          };
          setState({ rating, loading: false, error: null });
        } else {
          setState({ rating: null, loading: false, error: null });
        }
      },
      (err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch quality rating';
        console.error('Error in quality rating subscription:', err);
        setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, routeId]);

  /**
   * Submit or update a rating for the route
   */
  const submitRating = useCallback(
    async (ratingValue: QualityRatingValue): Promise<void> => {
      if (!user) {
        throw new Error('Must be logged in to submit rating');
      }

      if (!routeId) {
        throw new Error('Route ID is required');
      }

      setIsSubmitting(true);

      try {
        const db = getFirebaseDb();
        const ratingId = getQualityRatingId(user.uid, routeId);
        const ratingRef = doc(db, 'qualityRatings', ratingId);

        const isNewRating = !state.rating;

        const ratingData = {
          id: ratingId,
          odKey: ratingId,
          userId: user.uid,
          routeId,
          rating: ratingValue,
          ...(isNewRating
            ? { createdAt: serverTimestamp() }
            : { createdAt: state.rating?.createdAt }),
          updatedAt: serverTimestamp(),
        };

        await setDoc(ratingRef, ratingData, { merge: true });
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, routeId, state.rating]
  );

  /**
   * Remove the user's rating
   */
  const removeRating = useCallback(async (): Promise<void> => {
    if (!user) {
      throw new Error('Must be logged in to remove rating');
    }

    if (!routeId) {
      throw new Error('Route ID is required');
    }

    setIsSubmitting(true);

    try {
      const db = getFirebaseDb();
      const ratingId = getQualityRatingId(user.uid, routeId);
      const ratingRef = doc(db, 'qualityRatings', ratingId);

      await deleteDoc(ratingRef);
    } finally {
      setIsSubmitting(false);
    }
  }, [user, routeId]);

  return {
    ...state,
    submitRating,
    removeRating,
    isSubmitting,
  };
};

export default useQualityRating;
